/**
 * EFFET : le filtre barbichette — MA main cartoon pince le menton de mon AMI,
 * dessinée par-dessus son image (vidéo reçue ou maillage). Purement visuel :
 * la détection du jeu tourne sur MA vidéo brute, jamais sur une image filtrée.
 */
import gloveUrl from '../assets/glove.svg';
import type { Point3 } from '../detection/types';
import { chinAnchor, GLOVE, placeHand, type HandPlacement } from './chinAnchor';
import { handAlpha } from './handPresence';
import { createPlacementSmoother } from './oneEuro';
import { createPerfGovernor, DETECTION_FPS, type TrackingLevel } from './perfGovernor';
import { makeProjector } from './projection';

/** Visage de l'ami tel qu'il est affiché. */
export interface RemoteFace {
  /** Points du visage (normalisés) et instant de leur mesure ; null = jamais vu. */
  latest(): { readonly points: readonly Point3[]; readonly t: number } | null;
  /** Ratio largeur/hauteur de l'image source, et si elle est affichée en miroir. */
  readonly sourceAspect: () => number;
  readonly mirror: () => boolean;
}

export interface BarbichetteOverlay {
  readonly element: HTMLCanvasElement;
  /** Cadence d'analyse souhaitée pour la vidéo de l'ami (0 = inutile maintenant). */
  detectionFps(): number;
  stop(): void;
}

/**
 * Build e2e uniquement : `?perf=full|reduced|fixed` impose le niveau de suivi.
 * La dégradation est testée unitairement ; les e2e ne doivent pas dépendre de
 * la vitesse de la machine de CI.
 */
export function e2ePinnedLevel(): TrackingLevel | undefined {
  if (import.meta.env['VITE_E2E'] !== 'true') return undefined;
  const p = new URLSearchParams(location.search).get('perf');
  return p === 'full' || p === 'reduced' || p === 'fixed' ? p : undefined;
}

export function createBarbichetteOverlay(options: {
  face: () => RemoteFace;
  /** Main visible (phase de jeu) et filtre activé par le joueur. */
  active: () => boolean;
  enabled: () => boolean;
  /** Niveau imposé (tests e2e) au lieu de la mesure de cadence. */
  pinnedLevel?: TrackingLevel | undefined;
}): BarbichetteOverlay {
  const canvas = document.createElement('canvas');
  canvas.className = 'overlay';
  canvas.dataset['testid'] = 'barbichette';
  canvas.setAttribute('aria-hidden', 'true');
  const ctx = canvas.getContext('2d');
  const glove = new Image();
  glove.src = gloveUrl;

  const governor = createPerfGovernor();
  const smoother = createPlacementSmoother();
  let level: TrackingLevel = 'full';
  let placement: HandPlacement | null = null;
  let lastMeasure = -Infinity;
  let lastSeen = -Infinity;
  let raf = 0;
  /** Clé de ce qui est dessiné actuellement (null : à redessiner). */
  let painted: string | null = null;

  const setState = (state: string): void => {
    if (canvas.dataset['state'] !== state) canvas.dataset['state'] = state;
  };

  /** Main fixe approximative (plus de suivi) : dernière position connue, sinon bas-centre. */
  const fixedPlacement = (w: number, h: number): HandPlacement =>
    placement ?? {
      x: w / 2,
      y: h * 0.78,
      angle: 0,
      width: w * 0.28,
      height: w * 0.28 * GLOVE.aspect,
      clamped: false,
    };

  const draw = (now: number): void => {
    raf = requestAnimationFrame(draw);
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      painted = null; // redimensionner efface le canvas
    }
    if (!ctx) return;
    // Ne redessine que si l'image change : la main bouge au rythme des mesures (≤ 15/s),
    // pas à chaque image de l'écran.
    const paint = (key: string, fn?: (c: CanvasRenderingContext2D) => void): void => {
      if (key === painted) return;
      painted = key;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      fn?.(ctx);
    };
    if (!options.enabled()) {
      setState('off');
      paint('off');
      return;
    }
    if (!options.active() || !(w > 0 && h > 0)) {
      setState('inactive');
      paint('inactive');
      return;
    }
    // Cadence mesurée seulement quand la main est affichée : la connexion, la
    // calibration ou le chargement du modèle ne doivent pas dégrader le filtre.
    level = options.pinnedLevel ?? governor.frame(now);
    canvas.dataset['level'] = level;

    let alpha = 1;
    let hand: HandPlacement;
    if (level === 'fixed') {
      hand = fixedPlacement(w, h);
      setState('fixed');
    } else {
      const face = options.face();
      const m = face.latest();
      if (m && m.t > lastMeasure) {
        lastMeasure = m.t;
        const project = makeProjector({
          width: w,
          height: h,
          sourceAspect: face.sourceAspect(),
          fit: 'cover',
          mirror: face.mirror(),
        });
        const anchor = m.points.length > 0 ? chinAnchor(m.points, project) : null;
        if (anchor) {
          // Retour après une disparition complète : pas de glissade depuis l'ancienne position.
          if (handAlpha(m.t - lastSeen) === 0) smoother.reset();
          placement = smoother.push(placeHand(anchor), m.t);
          lastSeen = m.t;
        }
      }
      alpha = placement ? handAlpha(now - lastSeen) : 0;
      if (!placement || alpha === 0) {
        setState('hidden');
        paint('hidden');
        return;
      }
      hand = placement;
      setState(alpha < 1 ? 'fading' : 'tracking');
    }
    canvas.dataset['clamped'] = String(hand.clamped);
    if (!glove.complete) return;
    const key = [hand.x, hand.y, hand.width].map((v) => v.toFixed(1)).join(',');
    paint(`${key},${hand.angle.toFixed(3)},${alpha.toFixed(2)}`, (c) => {
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      c.globalAlpha = alpha;
      c.translate(hand.x, hand.y);
      c.rotate(hand.angle);
      c.drawImage(
        glove,
        -GLOVE.pinch.x * hand.width,
        -GLOVE.pinch.y * hand.height,
        hand.width,
        hand.height,
      );
      c.globalAlpha = 1;
    });
  };
  raf = requestAnimationFrame(draw);

  return {
    element: canvas,
    detectionFps: () => (options.enabled() && options.active() ? DETECTION_FPS[level] : 0),
    stop() {
      cancelAnimationFrame(raf);
    },
  };
}
